/**
 * @file ChallengeReadingTab.jsx
 * @description Reading tab component for MaterialChallenge
 */

import React, { useEffect, useRef, useState } from "react";
import { formatDateString } from "../../../utils/dateUtils";
import { formatReadingReferences } from "../../../utils/bible_language_utils";
import { getBibleLanguageFromCookie } from "../../../utils/theme_utils";
import { BIBLE_LANGUAGES } from "../../../constants";
import { formatCopiedVerseReference } from "../../../utils/lsm_api";
import { stripBrackets, stripFootnotes } from "../../../utils/verse_format_utils";
import { ChallengeReadingContent } from "./ChallengeReadingContent";
import { Icon } from "../../Icon";

// Helper function to format reading titles with language awareness
const formatReadingTitle = (readings) => {
  const bibleLanguage = getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;
  return formatReadingReferences(readings, bibleLanguage);
};

const copyTextToClipboard = async (text) => {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      if (typeof document.execCommand !== 'function') {
        throw error;
      }
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    if (!document.execCommand('copy')) {
      throw new Error('Copy command failed');
    }
  } finally {
    document.body.removeChild(textArea);
  }
};

export const ChallengeReadingTab = ({
  currentDay,
  datesToChapters,
  userChallengeData,
  onPrevious,
  onNext,
  onGoToFirstUnread,
  onGoToToday,
  onMarkComplete,
  bibleCache,
  fetchBibleTextForDay,
  loadingDays,
  isMarkingComplete,
  userIsInChallenge,
  verseTextSizes,
  user,
  onSignIn,
  bookmark,
  onSaveBookmark,
  onRemoveBookmark,
  isSavingBookmark,
  showToastMessage
}) => {
  const readingContainerRef = useRef(null);
  const [activeVerse, setActiveVerse] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [pendingBookmark, setPendingBookmark] = useState(null);

  useEffect(() => {
    if (!readingContainerRef.current) {
      return;
    }
    if (typeof readingContainerRef.current.scrollTo === 'function') {
      readingContainerRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }
    readingContainerRef.current.scrollTop = 0;
    readingContainerRef.current.scrollLeft = 0;
  }, [currentDay]);

  useEffect(() => {
    setActiveVerse(null);
    setTooltipPosition(null);
    setShowBookmarkDialog(false);
    setPendingBookmark(null);
  }, [currentDay]);
  if (datesToChapters.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <md-circular-progress indeterminate></md-circular-progress>
        <p style={{ marginTop: '16px', color: 'var(--md-sys-color-on-surface-variant)' }}>
          Loading reading plan...
        </p>
      </div>
    );
  }

  const currentDayData = datesToChapters[currentDay];
  const isRead = userChallengeData && userChallengeData.progress && userChallengeData.progress[currentDay] === '1';
  const canBookmark = Boolean(user && userIsInChallenge && !userChallengeData?.archived);
  const matchesBookmark = (verse) => {
    if (!bookmark || !verse) {
      return false;
    }
    return String(bookmark.book).toLowerCase() === String(verse.book).toLowerCase()
      && Number(bookmark.chapter) === Number(verse.chapter)
      && Number(bookmark.verse) === Number(verse.verse);
  };
  const activeVerseIsBookmarked = matchesBookmark(activeVerse);
  const bookmarkLabel = bookmark
    ? `${bookmark.book} ${bookmark.chapter}:${bookmark.verse}`
    : null;
  const activeVerseLabel = activeVerse
    ? `${activeVerse.book} ${activeVerse.chapter}:${activeVerse.verse}`
    : null;

  const handleCopyVerse = async () => {
    if (!activeVerse) {
      return;
    }

    const reference = formatCopiedVerseReference(
      activeVerse.referenceBook || activeVerse.book,
      activeVerse.chapter,
      activeVerse.verse
    );
    const verseText = stripBrackets(stripFootnotes(activeVerse.text || ''))
      .replace(/\s+/g, ' ')
      .trim();
    const copyText = `${reference} ${verseText}`.trim();

    try {
      await copyTextToClipboard(copyText);
      if (showToastMessage) {
        showToastMessage('Verse copied.');
      }
      closeTooltip();
    } catch (error) {
      console.error('Failed to copy verse:', error);
      if (showToastMessage) {
        showToastMessage('Failed to copy verse.');
      }
    }
  };

  const handleVerseClick = (verse, element) => {
    if (!element || !readingContainerRef.current) {
      return;
    }
    const containerRect = readingContainerRef.current.getBoundingClientRect();
    const verseRect = element.getBoundingClientRect();
    const top = verseRect.top - containerRect.top + readingContainerRef.current.scrollTop;
    const left = verseRect.left - containerRect.left + readingContainerRef.current.scrollLeft;
    setActiveVerse(verse);
    setTooltipPosition({
      top,
      left,
      width: verseRect.width,
      height: verseRect.height
    });
  };

  const closeTooltip = () => {
    setActiveVerse(null);
    setTooltipPosition(null);
  };

  const handleBookmarkAction = () => {
    if (!activeVerse) {
      return;
    }
    if (!canBookmark) {
      return;
    }
    if (activeVerseIsBookmarked) {
      onRemoveBookmark();
      closeTooltip();
      return;
    }
    if (bookmark && !activeVerseIsBookmarked) {
      setPendingBookmark(activeVerse);
      setShowBookmarkDialog(true);
      return;
    }
    onSaveBookmark({
      book: activeVerse.book,
      chapter: activeVerse.chapter,
      verse: activeVerse.verse
    });
    closeTooltip();
  };

  const confirmReplaceBookmark = () => {
    if (!pendingBookmark) {
      return;
    }
    onSaveBookmark({
      book: pendingBookmark.book,
      chapter: pendingBookmark.chapter,
      verse: pendingBookmark.verse
    });
    setPendingBookmark(null);
    setShowBookmarkDialog(false);
    closeTooltip();
  };

  const cancelReplaceBookmark = () => {
    setPendingBookmark(null);
    setShowBookmarkDialog(false);
    if (showToastMessage && bookmarkLabel) {
      showToastMessage(`Bookmark kept at ${bookmarkLabel}.`);
    }
  };
  
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Day navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderBottom: '1px solid var(--md-sys-color-outline-variant)',
        backgroundColor: 'var(--md-sys-color-surface-container-low)'
      }}>
        <button 
          onClick={onPrevious} 
          {...(currentDay === 0 ? { disabled: true } : {})}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            borderRadius: '50%',
            cursor: currentDay === 0 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: currentDay === 0 ? 'var(--md-sys-color-outline)' : 'var(--md-sys-color-on-surface)',
            minWidth: '48px',
            minHeight: '48px'
          }}
        >
          <Icon name="back" />
        </button>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 'var(--app-text-size-heading)', fontWeight: '500', color: 'var(--md-sys-color-on-surface)' }}>
              Day {currentDay + 1}
            </span>
            {/* Read indicator */}
            {isRead ? (
              <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
            ) : (
              <span style={{ 
                width: '8px', 
                height: '8px', 
                borderRadius: '50%', 
                backgroundColor: '#F44336',
                display: 'inline-block'
              }}></span>
            )}
          </div>
          <span style={{ fontSize: 'var(--app-text-size-small)', color: 'var(--md-sys-color-on-surface-variant)' }}>
            {currentDayData && formatDateString(currentDayData[0])}
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {userIsInChallenge && (
              <md-text-button onClick={onGoToFirstUnread} style={{ fontSize: 'var(--app-text-size-small)', height: 'var(--app-text-size-button-height)' }}>
                Go to 1st unread
              </md-text-button>
            )}
            <md-text-button onClick={onGoToToday} style={{ fontSize: 'var(--app-text-size-small)', height: 'var(--app-text-size-button-height)' }}>
              Go to today
            </md-text-button>
          </div>
        </div>
        
        <button 
          onClick={onNext} 
          {...(currentDay === datesToChapters.length - 1 ? { disabled: true } : {})}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            borderRadius: '50%',
            cursor: currentDay === datesToChapters.length - 1 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: currentDay === datesToChapters.length - 1 ? 'var(--md-sys-color-outline)' : 'var(--md-sys-color-on-surface)',
            minWidth: '48px',
            minHeight: '48px'
          }}
        >
          <Icon name="next" />
        </button>
      </div>
      
      {/* Reading content */}
      <div
        ref={readingContainerRef}
        data-testid="reading-scroll-container"
        style={{ flex: 1, overflow: 'auto', padding: '16px', position: 'relative' }}
        onClick={() => {
          if (activeVerse) {
            closeTooltip();
          }
        }}
        onScroll={() => {
          if (activeVerse) {
            closeTooltip();
          }
        }}
      >
        {currentDayData && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <h2 style={{
                fontSize: 'var(--app-text-size-title)',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                margin: 0
              }}>
                {formatReadingTitle(currentDayData[1])}
              </h2>
            </div>
            
            <div style={{
              backgroundColor: 'var(--md-sys-color-surface-container-low)',
              borderRadius: '12px',
              padding: '16px',
              lineHeight: '1.6'
            }}>
              <ChallengeReadingContent 
                key={currentDay}
                readings={currentDayData[1]} 
                dayIndex={currentDay}
                bibleCache={bibleCache}
                fetchBibleTextForDay={fetchBibleTextForDay}
                loadingDays={loadingDays}
                textSizes={verseTextSizes}
                bookmark={bookmark}
                onVerseClick={handleVerseClick}
                readingContainerRef={readingContainerRef}
              />
            </div>
            {activeVerse && tooltipPosition && (
              <div
                data-testid="bookmark-tooltip"
                style={{
                  position: 'absolute',
                  top: Math.max(8, tooltipPosition.top - 56),
                  left: Math.max(8, tooltipPosition.left),
                  backgroundColor: 'var(--md-sys-color-surface-container-high)',
                  color: 'var(--md-sys-color-on-surface)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  zIndex: 2,
                  minWidth: '200px'
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <div style={{ fontSize: 'var(--app-text-size-small)', fontWeight: '600', marginBottom: '8px' }}>
                  {activeVerseLabel}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <md-text-button
                    data-testid="bookmark-action"
                    onClick={handleBookmarkAction}
                    {...((!canBookmark || isSavingBookmark) ? { disabled: true } : {})}
                    style={{ height: 'var(--app-text-size-button-height)' }}
                  >
                    <Icon name="bookmark" slot="icon" />
                    {activeVerseIsBookmarked ? 'Remove bookmark' : 'Save my place'}
                  </md-text-button>
                  <md-text-button
                    data-testid="copy-verse-action"
                    onClick={handleCopyVerse}
                    style={{ height: 'var(--app-text-size-button-height)' }}
                  >
                    <Icon name="copy" slot="icon" />
                    Copy verse
                  </md-text-button>
                </div>
                {!canBookmark && (
                  <div style={{ marginTop: '8px', fontSize: 'var(--app-text-size-small)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                    {!user
                      ? 'Sign in to save bookmarks.'
                      : userChallengeData?.archived
                        ? 'Archived challenges cannot save bookmarks.'
                        : 'Join this challenge to save bookmarks.'}
                  </div>
                )}
              </div>
            )}

            {/* Mark Complete Button at Bottom - Only show if user is in challenge and not archived */}
            {userIsInChallenge && !userChallengeData?.archived && user && (
              <div style={{ 
                marginTop: '24px', 
                textAlign: 'center',
                paddingBottom: '16px'
              }}>
                <button 
                  onClick={() => onMarkComplete(currentDay)}
                  {...(isMarkingComplete ? { disabled: true } : {})}
                  style={{ 
                    backgroundColor: isMarkingComplete ? 'var(--md-sys-color-surface-variant)' : 
                                    isRead ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-primary)',
                    color: isMarkingComplete ? 'var(--md-sys-color-on-surface-variant)' :
                           isRead ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-primary)',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '12px 24px',
                    fontSize: 'var(--app-text-size-small)',
                    fontWeight: '500',
                    minWidth: '200px',
                    cursor: isMarkingComplete ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: 'var(--md-sys-typescale-label-large-font)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1px',
                    opacity: isMarkingComplete ? 0.7 : 1
                  }}
                >
                  {isMarkingComplete ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid currentColor',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '18px' }}>
                        {isRead ? '✓' : '✓'}
                      </span>
                      {isRead ? 'Mark Incomplete' : 'Mark Complete'}
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* Sign In Button at Bottom - Show if user is not logged in */}
            {!user && (
              <div style={{ 
                marginTop: '24px', 
                textAlign: 'center',
                paddingBottom: '16px'
              }}>
                <button 
                  onClick={onSignIn}
                  style={{ 
                    backgroundColor: 'var(--md-sys-color-primary)',
                    color: 'var(--md-sys-color-on-primary)',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '12px 24px',
                    fontSize: 'var(--app-text-size-small)',
                    fontWeight: '500',
                    minWidth: '200px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: 'var(--md-sys-typescale-label-large-font)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1px'
                  }}
                >
                  <Icon name="signIn" style={{ fontSize: '18px' }} />
                  Sign In
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {showBookmarkDialog && (
        <md-dialog open onClosed={cancelReplaceBookmark}>
          <div slot="headline">Replace bookmark?</div>
          <div slot="content" style={{ color: 'var(--md-sys-color-on-surface)' }}>
            {bookmarkLabel
              ? `This will replace your existing bookmark at ${bookmarkLabel}.`
              : 'This will replace your existing bookmark.'}
          </div>
          <div slot="actions">
            <md-text-button onClick={cancelReplaceBookmark}>Cancel</md-text-button>
            <md-filled-button onClick={confirmReplaceBookmark}>Replace</md-filled-button>
          </div>
        </md-dialog>
      )}
    </div>
  );
};
